<template>
  <div class="p-message-predict-summary">
    <div class="c-error c-error--balloon c-error--reach-count" :class="{'is-active': errorMessage !== '', 'c-error--reach-count-info': failureTone === 'info' }">
      <div class="c-error__text">{{ errorMessage }}</div>
    </div>
    <div class="p-message-predict-summary__title">配信される予測数</div>
    <div v-show="isLoding" class="p-message-predict-summary__retry">
      <div class="c-loader c-loader"></div>
      <div class="p-message-predict-summary__retry-body">
        <div class="p-message-predict-summary__retry-main">予測数を確認しています</div>
        <div v-show="isRetrying" class="p-message-predict-summary__retry-sub">確認に少し時間がかかっています</div>
      </div>
    </div>
    <div v-show="!isLoding" class="p-message-predict-summary-list">
      <div class="p-message-predict-summary-list__item">
        <span class="p-message-predict-summary__text p-message-predict-summary__text--value">{{ reachCountValue }}</span>
        <span class="p-message-predict-summary__text p-message-predict-summary__text--unit">件</span>
      </div>
      <div class="p-message-predict-summary-list__item">
        <span class="p-message-predict-summary__text">オプトイン数の</span>
        <span class="p-message-predict-summary__text p-message-predict-summary__text--ratio">{{ optinRatio }}</span>
        <span class="p-message-predict-summary__text">%</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

// 予測数取得のリトライ／タイムアウト設定
const MAX_ATTEMPTS = 2; // 合計試行回数（初回 + リトライ1回）
const RETRY_DELAY = 2000; // リトライまでの待機時間(ms)
// サーバ側は60秒でタイムアウトしエラーを返すため、クライアントはその少し上(65秒)を安全網として設定する
const REQUEST_TIMEOUT = 65000; // 1リクエストあたりのタイムアウト(ms)

export default {
  props: {
    messageId: {
      type: Number,
      required: false,
      default: null
    },
    failureMessage: {
      type: String,
      required: false,
      default: '配信される予測数を取得できませんでした。'
    },
    // 吹き出しのトーン。'error'（赤・やり直し系）／'info'（青・お知らせ系）。
    failureTone: {
      type: String,
      required: false,
      default: 'error',
      validator: value => ['error', 'info'].includes(value)
    }
  },
  emits: ["loading-change"],
  data() {
    return {
      isLoding: true,
      optinCountValue: 0,
      reachCountValue: 0,
      errorMessage: '',
      isRetrying: false,
      requestSeq: 0
    }
  },
  computed: {
    optinRatio: function() {
      if (isNaN(this.reachCountValue)) {
        return '-';
      }
      const value = this.reachCountValue / this.optinCountValue * 100;
      if (isNaN(value)) {
        return 0;
      }
      return value.toFixed(2);
    },
  },
  watch: {
    isLoding: {
      immediate: true,
      handler(val) {
        this.$emit('loading-change', val);
      }
    }
  },
  mounted() {
    this.getReachCount();
  },
  methods: {
    updateReachCount: function(value) {
      if (value > 100) {
        this.animateCountUp(value, 'reachCountValue')
      } else {
        this.reachCountValue = value;
      }
    },
    animateCountUp(value, key) {
      const duration = 1000;
      const startTime = Date.now();
      var interval = 35;

      const timer = setInterval(() => {
        let randomValue = Math.floor(Math.random() * value - 1);
        const elapsedTime = Date.now() - startTime;

        this[key] = randomValue;

        if (elapsedTime >= duration) {
          this[key] = value;
          clearInterval(timer);
        }
        interval++;
      }, interval);
    },
    getReachCount(params) {
      this.isLoding = true;
      this.errorMessage = '';
      this.isRetrying = false;
      // 親の this.params を参照のまま保持すると、リトライ待機中に条件が変更された場合に
      // 同一シーケンスのリトライが別条件で送信されてしまうため、ここで固定する。
      const baseParams = params || {};
      const requestParams = {
        ...baseParams,
        conditions: Array.isArray(baseParams.conditions) ? [...baseParams.conditions] : baseParams.conditions,
        topicConditions: Array.isArray(baseParams.topicConditions) ? [...baseParams.topicConditions] : baseParams.topicConditions,
      };
      if (this.messageId) {
        requestParams.message_id = this.messageId;
      }
      this.requestSeq++;
      this.fetchWithRetry(requestParams, 1, this.requestSeq);
    },
    fetchWithRetry(params, attempt, seq) {
      axios.get('/api/reach-count', {params, timeout: REQUEST_TIMEOUT})
        .then(res => {
          if (seq !== this.requestSeq) return; // 新しい検索が始まっていたら破棄
          const data = res.data && res.data.data;
          if (! data || typeof data.optin_count === 'undefined' || typeof data.reach_count === 'undefined') {
            const e = new Error('Invalid response data.');
            e.isInvalidResponse = true;
            throw e;
          }
          this.isLoding = false;
          this.isRetrying = false;
          this.optinCountValue = data.optin_count;
          this.updateReachCount(data.reach_count);
        })
        .catch(error => {
          if (seq !== this.requestSeq) return; // 新しい検索が始まっていたら破棄

          const status = error.response ? error.response.status : null;
          // タイムアウト(504/axiosのECONNABORTED）はリトライしない。リトライすると合計2分近く待たせ、DB負荷も倍になるため
          // レスポンス構造不正は再送しても直らないためリトライしない
          // 一時的な通信断や 5xx（504除く）はリトライ対象とする
          const isTimeout = error.code === 'ECONNABORTED' || status === 504 || status === 408;
          const isRetryable = ! isTimeout && ! error.isInvalidResponse && (! error.response || status >= 500);
          if (isRetryable && attempt < MAX_ATTEMPTS) {
            this.isRetrying = true;
            setTimeout(() => {
              if (seq !== this.requestSeq) return; // 待機中に新しい検索が来たら中断
              this.fetchWithRetry(params, attempt + 1, seq);
            }, RETRY_DELAY);
            return;
          }

          this.isLoding = false;
          this.isRetrying = false;
          this.optinCountValue = '-';
          this.reachCountValue = '-';
          this.errorMessage = this.failureMessage;
        });
    },
  },
}
</script>
